import React, {useCallback, useEffect, useRef, useState} from 'react';
import Header from '../../components/Header';
import Search from '../../components/Search';
import { Link } from 'react-router-dom';
import {CategoryDTO} from "../../dtos/category.dto";
import {OrderDTO, OrderState} from "../../dtos/order.dto";
import {BaseItemDTO} from "../../dtos/item.dto";
import {toast} from "react-toastify";
import {API_URL} from "../../utils/constants";
import {useRecoilState, useRecoilValue} from "recoil";
import {adminState, fetchAPIResponseSelector, ordersFilterState} from "../../store/user.atom";
import {useNavigate} from "react-router";
import {io, Socket} from "socket.io-client";
import DatePicker from "react-datepicker";
import {STATUS_META} from "../../utils/orderStatus";
import OrderStateSelect from "../../components/OrderStateSelect";

const Orders = () => {
    const [events, setEvents] = useState<string[]>([]);
    const [orders, setOrders] = useState<OrderDTO[]>([]);
    const [adminDTO] = useRecoilState(adminState);

    const [page, setPage] = useState<number>(1);
    const [size, setSize] = useState<number>(10);
    const [totalPages, setTotalPages] = useState<number>(1);
    const observer = useRef<IntersectionObserver | null>(null);
    const fetchAPIResponse = useRecoilValue(fetchAPIResponseSelector);

    const navigate = useNavigate();
    const [socket, setSocket] = useState<Socket | null>(null);

    // Filter lives in a Recoil atom so it persists when navigating to an
    // order's details and back (the component unmounts in between).
    const [filters, setFilters] = useRecoilState(ordersFilterState);
    const { orderState, startDate, endDate } = filters;
    const setOrderState = (v: OrderState | undefined) => setFilters(f => ({ ...f, orderState: v }));
    const setStartDate = (v: Date | undefined) => setFilters(f => ({ ...f, startDate: v }));
    const setEndDate = (v: Date | undefined) => setFilters(f => ({ ...f, endDate: v }));


    useEffect(() => {
        if (adminDTO?.token) {
            const newSocket = io(API_URL, {
                query: { token: adminDTO.token },
                transports: ['websocket']
            });

            setSocket(newSocket);

            const notificationSound = new Audio('/notification.mp3');

            newSocket.on('order', (order) => {
                setOrders((orders) => [order, ...orders]);

                // Play notification sound
                notificationSound.play().catch(error => console.error('Error playing sound:', error));
            });

            newSocket.on('connect_error', (error) => {
                console.error('Connection error:', error);
            });

            return () => {
                newSocket.close();
            }
        }
    }, []);


    useEffect(() => {
        fetchOrders(true)
    }, [orderState, startDate, endDate]);

    useEffect(() => {
        if(page !== 1) {
            fetchOrders(false)
        }
    }, [page]);

    const fetchOrders = async (reset: boolean = false) => {
        try {
            let fetchUrl = `/orders/?size=${size}&from=${startDate}&to=${endDate}`;

            if(orderState !== undefined) {
                fetchUrl += `&state=${orderState}`
            }

            if(reset) {
                fetchUrl += `&page=1`
            } else {
                fetchUrl += `&page=${page}`
            }

            const response = await fetchAPIResponse<{
                pagination_options: any,
                contents: OrderDTO[]
            }>(fetchUrl, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json"
                },
            });

            setOrders(prevItems => (reset ? response.data.contents : [...prevItems, ...response.data.contents]));
            setTotalPages(response.data.pagination_options.total_pages);

            if(reset) {
                setPage(1)
            }
        } catch (error) {
            console.error('Error fetching items:', error);
        }
    };

    const lastItemRef = useCallback((node: HTMLDivElement | null) => {
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && page < totalPages) {
                setPage(prevPage => prevPage + 1);
            }
        });
        if (node) observer.current.observe(node);
    }, [page, totalPages]);

    const handleEdit = (itemId: any) => {
        navigate(`/items/edit/${itemId}`);
    };

    const handleDelete = async (itemId: any) => {
        const confirmDelete = window.confirm('Are you sure you want to delete this item?');
        if (confirmDelete) {
            try {
                await fetchAPIResponse(`/items/admin/${itemId}`, {
                    method: 'DELETE',
                    headers: {
                        'Accept': 'application/json'
                    }
                });

                setPage(1)
                setOrders([])
                fetchOrders();
            } catch (error) {
                toast.error((error as Error).message);
            }
        }
    };

    const handleStateChange = async (orderId: string, newState: OrderState) => {
        try {
            setOrders(orders.map(r => {
                if(r.id === orderId) {
                    r.state = newState as OrderState;
                }

                return r;
            }));

            await fetchAPIResponse(`/orders/${orderId}/status`, {
                method: 'PUT',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    toState: newState
                })
            });

        } catch (error) {
            toast.error((error as Error).message);
        }
    };

    const meta = (state: OrderState) => STATUS_META[state] ?? STATUS_META.PENDING;

    return (
        <div className='flex flex-col items-center w-full h-full overflow-auto py-10 gap-6'>
            <Header title={'Orders'} button={false} buttonTitle={''} action={''} />

            {/* Filter toolbar */}
            <div className="zbh-card w-11/12 p-5">
                <div className="flex flex-wrap items-end gap-5">
                    <div className="flex flex-col">
                        <label className="zbh-eyebrow mb-1.5">Status</label>
                        <select
                            value={orderState}
                            onChange={(e) => setOrderState(e.target.value as OrderState)}
                            className="zbh-input w-48"
                        >
                            <option value="">All order states</option>
                            {Object.values(OrderState).map((state) => (
                                <option key={state} value={state}>
                                    {state.charAt(0) + state.slice(1).toLowerCase()}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="flex flex-col">
                        <label htmlFor="startDate" className="zbh-eyebrow mb-1.5">From date</label>
                        <DatePicker
                            id="startDate"
                            selected={startDate}
                            onChange={date => { if (date != null) setStartDate(date) }}
                            selectsStart startDate={startDate} endDate={endDate} isClearable
                            placeholderText="Start date"
                            className="zbh-input w-48"
                        />
                    </div>
                    <div className="flex flex-col">
                        <label htmlFor="endDate" className="zbh-eyebrow mb-1.5">To date</label>
                        <DatePicker
                            id="endDate"
                            selected={endDate}
                            onChange={date => { if (date != null) setEndDate(date) }}
                            selectsEnd startDate={startDate} endDate={endDate} isClearable
                            placeholderText="End date"
                            className="zbh-input w-48"
                        />
                    </div>
                    <div className="ml-auto self-center text-right">
                        <p className="zbh-eyebrow">Showing</p>
                        <p className="zbh-figure text-lg font-bold text-ink">{orders.length}</p>
                    </div>
                </div>
            </div>

            {/* Ticket rail */}
            <div className="w-11/12 flex flex-col gap-3 pb-6">
                {/* column legend */}
                <div className="hidden md:grid grid-cols-12 gap-4 px-6">
                    <div className="zbh-th col-span-3">Placed</div>
                    <div className="zbh-th col-span-3">Customer</div>
                    <div className="zbh-th col-span-2">Items</div>
                    <div className="zbh-th col-span-1 text-right">Total</div>
                    <div className="zbh-th col-span-2">Status</div>
                    <div className="zbh-th col-span-1 text-right">&nbsp;</div>
                </div>

                {orders.length === 0 && (
                    <div className="zbh-card flex flex-col items-center justify-center gap-1 py-16 text-center">
                        <p className="font-display text-lg font-semibold text-ink">No orders in this window</p>
                        <p className="text-sm text-categoriesTextColor">Adjust the date range or status filter to see tickets.</p>
                    </div>
                )}

                {orders.map((order, index) => {
                    const m = meta(order.state);
                    return (
                        <div
                            key={order.id}
                            className="relative overflow-hidden rounded-xl border border-hair bg-surface shadow-card animate-ticket-in transition hover:shadow-lift"
                            ref={index === orders.length - 1 ? lastItemRef : null}
                        >
                            <span className={`absolute left-0 top-0 h-full w-1.5 ${m.spine}`} />
                            <div className="grid grid-cols-12 items-center gap-4 pl-6 pr-5 py-4">
                                {/* Placed */}
                                <div className="col-span-12 md:col-span-3">
                                    <p className="zbh-figure text-sm text-ink">
                                        {new Date(order.order_date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                    <p className="text-xs text-categoriesTextColor">
                                        {new Date(order.order_date).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })}
                                        <span className="zbh-figure"> · #{order.id.slice(-6)}</span>
                                    </p>
                                </div>
                                {/* Customer */}
                                <div className="col-span-12 md:col-span-3 flex items-center gap-3">
                                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-soft text-accent-ink text-xs font-bold">
                                        {order.user?.name?.trim()?.charAt(0)?.toUpperCase() ?? '?'}
                                    </span>
                                    <div className="min-w-0">
                                        <p className="truncate text-sm font-semibold text-ink">{order.user?.name}</p>
                                        <p className="zbh-figure text-xs text-categoriesTextColor">{order.user?.phone_number}</p>
                                    </div>
                                </div>
                                {/* Items */}
                                <div className="col-span-12 md:col-span-2">
                                    <p className="text-sm text-ink">{order.items.length} item{order.items.length === 1 ? '' : 's'}</p>
                                    <p className="truncate text-xs text-categoriesTextColor">{order.items.map(i => i.name).join(', ')}</p>
                                </div>
                                {/* Total */}
                                <div className="col-span-6 md:col-span-1 md:text-right">
                                    <p className="zbh-figure text-sm font-bold text-ink">${order.total}</p>
                                </div>
                                {/* Status control */}
                                <div className="col-span-6 md:col-span-2">
                                    <OrderStateSelect
                                        value={order.state}
                                        onChange={(s) => handleStateChange(order.id, s)}
                                    />
                                </div>
                                {/* Action */}
                                <div className="col-span-6 md:col-span-1 md:text-right">
                                    <Link to={`/orders/view/${order.id}`} className="zbh-link text-sm">View</Link>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default Orders;
